import React from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Chip,
  Alert,
  CircularProgress,
  Button,
  Collapse,
} from "@mui/material";
import { MdClose } from "react-icons/md";
import ChipStatus from "../ChipStatus";

interface DiscountImpactTableProps {
  /** Single product preview result from bulk-discount-preview API (results[0]) */
  previewData: any;
  /** Whether the preview is currently loading */
  isLoading: boolean;
  /** Whether the table is visible */
  visible: boolean;
  /** Callback to hide the impact table */
  onClose?: () => void;
  /** When true, hides header, summary chips, and loss warning (used in BulkDiscountDialog) */
  compact?: boolean;
}

const DiscountImpactTable: React.FC<DiscountImpactTableProps> = ({
  previewData,
  isLoading,
  visible,
  onClose,
  compact = false,
}) => {
  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
        <CircularProgress size={28} />
        <Typography variant="body2" sx={{ ml: 2, color: "text.secondary" }}>
          Calculating pricing impact...
        </Typography>
      </Box>
    );
  }

  return (
    <Collapse in={visible && !!previewData} timeout="auto" unmountOnExit>
      {previewData && (
        <Box sx={{ mt: compact ? 0 : 2 }}>
          {!compact && (
            <>
              {/* Header */}
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                  Pricing Impact Preview
                </Typography>
                {onClose && (
                  <Button size="small" onClick={onClose} startIcon={<MdClose />} color="secondary">
                    Close
                  </Button>
                )}
              </Box>

              {/* Summary chips */}
              <Box sx={{ display: "flex", gap: 1, mb: 1.5, flexWrap: "wrap" }}>
                <Chip
                  label={`${previewData.denominations?.length || 0} Denominations`}
                  color="primary"
                  variant="outlined"
                  size="small"
                />
                {previewData.hasLoss && (
                  <Chip
                    label={`${previewData.lossWarnings?.length || 0} with Loss`}
                    color="error"
                    variant="filled"
                    size="small"
                  />
                )}
              </Box>

              {/* Loss warning */}
              {previewData.hasLoss && (
                <Alert severity="warning" sx={{ mb: 1.5 }}>
                  Some denominations will be sold at a loss with this discount configuration.
                </Alert>
              )}
            </>
          )}

          {/* Pricing table */}
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "#e3e8ef" }}>
                  <TableCell sx={{ fontWeight: "bold" }}>Denomination</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }} align="right">Face Value</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }} align="right">Cost Price</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }} align="right">Selling (Online)</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }} align="center">P/L (Online)</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }} align="right">Selling (Bank)</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }} align="center">P/L (Bank)</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }} align="right">Tax Rate</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {previewData.denominations?.map((d: any, idx: number) => (
                  <TableRow key={idx}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}>
                        {d.denomination?.productDenominationsku}
                      </Typography>
                      {d.isRange && (
                        <Typography variant="caption" sx={{ color: "info.main", fontSize: "0.6rem" }}>
                          Range: {(d.minValue || 0).toFixed(2)} - {(d.maxValue || 0).toFixed(2)} (shown at min)
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {(d.onlinePricing?.faceValue || 0).toFixed(2)}
                    </TableCell>
                    <TableCell align="right">
                      {(d.onlinePricing?.costPrice || 0).toFixed(2)}
                      {/*
                        With no supplier mapping the API returns provider "NONE"
                        and falls back to the face value as the cost -- i.e. "we
                        pay exactly what we charge". That is a placeholder, not a
                        cost, so say so rather than letting the number read as
                        real. The profit columns are blanked for the same reason.
                      */}
                      {d.onlinePricing?.provider === "NONE" && (
                        <Typography variant="caption" sx={{ display: "block", color: "warning.main", fontSize: "0.65rem", lineHeight: 1.3 }}>
                          No supplier mapped &mdash; cost unknown
                        </Typography>
                      )}
                      {/*
                        Where the cost came from. Without this the same number
                        appears whether it is the discount file the admin chose,
                        the next PIN in line after that file ran out, or a guess
                        with no stock at all — and only the first is what they
                        selected. The full explanation is on hover.
                      */}
                      {(() => {
                        const p = d.onlinePricing;
                        const src = p?.costSource;
                        if (!src || src === "selected-file" || src === "mapping") return null;

                        // Name the basis inline. "Estimated" on its own tells the
                        // admin nothing about where the number came from, which is
                        // the whole question they ask when they see it.
                        const disc =
                          p?.supplierDiscount === undefined || p?.supplierDiscount === null
                            ? ""
                            : p?.supplierDiscountType === "flat"
                              ? ` (${Number(p.supplierDiscount).toFixed(2)} flat)`
                              : ` (${p.supplierDiscount}%)`;

                        // Two lines on purpose: the FACT first (is there stock?),
                        // then the BASIS (where this number came from). Either
                        // half alone leaves the obvious next question unanswered.
                        const fact =
                          src === "fifo-next" ? "Selected file empty" : "No PINs in stock";
                        const basis =
                          src === "fifo-next"
                            ? `Using next PIN in line${disc}`
                            : src === "last-upload"
                              ? `Forecast \u2014 based on your last PIN upload${disc}`
                              : `Forecast \u2014 based on the mapped discount${disc}`;

                        return (
                          <Box title={p?.costNote} sx={{ cursor: "help" }}>
                            <Typography
                              variant="caption"
                              sx={{
                                display: "block",
                                color: src === "fifo-next" ? "warning.main" : "text.secondary",
                                fontSize: "0.65rem",
                                lineHeight: 1.3,
                                fontWeight: 600,
                              }}
                            >
                              {fact}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                display: "block",
                                color: src === "fifo-next" ? "warning.main" : "info.main",
                                fontSize: "0.65rem",
                                lineHeight: 1.3,
                              }}
                            >
                              {basis}
                            </Typography>
                          </Box>
                        );
                      })()}
                    </TableCell>
                    <TableCell align="right">
                      {(d.onlinePricing?.sellingPrice || 0).toFixed(2)}
                      <Typography variant="caption" sx={{ display: "block", color: "text.secondary", fontSize: "0.65rem" }}>
                        +tax {(d.onlinePricing?.taxAmount || 0).toFixed(2)} = {(d.onlinePricing?.priceWithTax ?? d.onlinePricing?.sellingPrice ?? 0).toFixed(2)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      {d.onlinePricing?.provider === "NONE" ? (
                        <Typography
                          variant="caption"
                          title="Map this denomination to a supplier to see the real margin"
                          sx={{ color: "text.disabled", fontSize: "0.75rem" }}
                        >
                          &mdash;
                        </Typography>
                      ) : (
                        <ChipStatus
                          status={d.onlinePricing?.isLoss ? "error" : "success"}
                          label={
                            d.onlinePricing?.isLoss
                              ? `-${(d.onlinePricing?.lossAmount || 0).toFixed(2)}`
                              : `+${(d.onlinePricing?.profit || 0).toFixed(2)}`
                          }
                        />
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {(d.bankTransferPricing?.sellingPrice || 0).toFixed(2)}
                      <Typography variant="caption" sx={{ display: "block", color: "text.secondary", fontSize: "0.65rem" }}>
                        +tax {(d.bankTransferPricing?.taxAmount || 0).toFixed(2)} = {(d.bankTransferPricing?.priceWithTax ?? d.bankTransferPricing?.sellingPrice ?? 0).toFixed(2)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      {d.bankTransferPricing?.provider === "NONE" ? (
                        <Typography
                          variant="caption"
                          title="Map this denomination to a supplier to see the real margin"
                          sx={{ color: "text.disabled", fontSize: "0.75rem" }}
                        >
                          &mdash;
                        </Typography>
                      ) : (
                        <ChipStatus
                          status={d.bankTransferPricing?.isLoss ? "error" : "success"}
                          label={
                            d.bankTransferPricing?.isLoss
                              ? `-${(d.bankTransferPricing?.lossAmount || 0).toFixed(2)}`
                              : `+${(d.bankTransferPricing?.profit || 0).toFixed(2)}`
                          }
                        />
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {d.onlinePricing?.taxRate
                        ? `${d.onlinePricing.taxRate.toFixed(2)}%`
                        : (
                          <Typography variant="caption" sx={{ color: "warning.main", fontSize: "0.65rem" }}>
                            No tax configured for this country
                          </Typography>
                        )}
                    </TableCell>
                  </TableRow>
                ))}
                {(!previewData.denominations || previewData.denominations.length === 0) && (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography variant="body2" sx={{ color: "text.secondary", py: 2 }}>
                        No denominations found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Collapse>
  );
};

export default DiscountImpactTable;
